import React from 'react';

import { Alert, Box, Button, Checkbox, FormControlLabel, Link, IconButton, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { UserInfoType } from '../../../common/type/UserInfoType';
import { appBar, alert, width, main } from '../../../common/const/componentSize';
import { redirectToOffsegLoginPage } from '../../../common/utils/redirectToOffseg';

import { useStateOffsegLoginAuthInfo } from '../hooks/useStateOffsegLoginAuthInfo';
import { useStateG500LiteRegistrationAuthInfo } from '../hooks/useStateG500LiteRegistrationAuthInfo';
import { useStateOffsegReregistrationAuthInfo } from '../hooks/useStateOffsegReregistrationAuthInfo';
import { useStateG500LiteReregistrationAuthInfo } from '../hooks/useStateG500LiteReregistrationAuthInfo';
import { useStateChecked } from '../hooks/useStateChecked';
import { useStateLoginError } from '../hooks/useStateLoginError';
import { useStateRegistrationError } from '../hooks/useStateRegistrationError';
import { useStateRegistrationDialog } from '../hooks/useStateRegistrationDialog';
import { useStateReregistrationStep1Dialog } from '../hooks/useStateReregistrationStep1Dialog';
import { useStateReregistrationStep2Dialog } from '../hooks/useStateReregistrationStep2Dialog';
import { usePostLogin } from '../hooks/usePostLogin';
import { usePostRegister } from '../hooks/usePostRegister';
import * as loginPageConst from '../../const/loginPageConst';
import { loginPageContent } from '../../const/loginPageSize';
import { LoginPageDialog } from '../parts/LoginPageDialog';
import { StyledTextField } from '../styled/loginPageStyled';

import theme from '../../../theme';


type LoginPagePropsType = {
	setUserInfo: React.Dispatch<React.SetStateAction<UserInfoType>>;
	setIsAsyncProcess: React.Dispatch<React.SetStateAction<boolean>>;
};

/**
 * ログインページ
 */
export const LoginPage = (props: LoginPagePropsType) => {
	const { setUserInfo, setIsAsyncProcess } = props;

	const { offsegLoginAuthInfo, setOffsegLoginAuthInfo } = useStateOffsegLoginAuthInfo(); // ログイン時のOffsegログイン情報の状態管理用カスタムフック
	const { offsegReregistrationAuthInfo, setOffsegReregistrationAuthInfo } = useStateOffsegReregistrationAuthInfo(); // 再登録時のOffsegログイン情報の状態管理用カスタムフック
	const { g500LiteRegistrationAuthInfo, setG500LiteRegistrationAuthInfo } = useStateG500LiteRegistrationAuthInfo(); //初回登録時のG500Liteログイン情報の状態管理用カスタムフック
	const { g500LiteReregistrationAuthInfo, setG500LiteReregistrationAuthInfo } = useStateG500LiteReregistrationAuthInfo(); // 再登録時のG500Liteログイン情報の状態管理用カスタムフック
	const { isChecked, onChangeCheckbox } = useStateChecked(); // ログイン情報記憶チェックボックスの状態管理用カスタムフック

	const {
		isLoginError,
		setIsLoginError,
		isLoginLockError,
		setIsLoginLockError,
		needsChangeInitPassword,
		setNeedsChangeInitPassword,
		isLocalStorageError,
		setIsLocalStorageError,
		onCloseLoginErrorAlert,
		onCloseLoginLockErrorAlert,
		onCloseLocalStorageErrorAlert
	} = useStateLoginError(); // ログインエラーの状態管理用カスタムフック
	const { isRegistrationError, setIsRegistrationError, onCloseRegistrationErrorAlert } = useStateRegistrationError(); // ログイン情報登録エラーの状態管理用カスタムフック

	const { isRegistrationDialogOpened, setIsRegistrationDialogOpened, onCloseRegistrationDialog } = useStateRegistrationDialog(setIsRegistrationError, setG500LiteRegistrationAuthInfo); // G500Liteログイン情報登録ダイアログの状態管理用カスタムフック
	const { isReregistrationDialogStep1Opened, setIsReregistrationDialogStep1Opened, onClickReregistrationLink, onCloseReregistrationDialogStep1 } =
		useStateReregistrationStep1Dialog({
			setIsLoginError,
			setNeedsChangeInitPassword,
			setIsLoginLockError,
			setIsLocalStorageError,
			setOffsegReregistrationAuthInfo
		}); // G500Liteログイン情報再登録STEP1ダイアログの状態管理用カスタムフック
	const {
		isReregistrationDialogStep2Opened,
		setIsReregistrationDialogStep2Opened,
		onCloseReregistrationDialogStep2,
		onClickReregistrationDialogStep2BackButton,
	} = useStateReregistrationStep2Dialog({
		setIsReregistrationDialogStep1Opened,
		setIsRegistrationError,
		setNeedsChangeInitPassword,
		setOffsegReregistrationAuthInfo,
		setG500LiteReregistrationAuthInfo
	}); // G500Liteログイン情報再登録STEP2ダイアログの状態管理用カスタムフック

	const usePostLoginInput = {
		setIsAsyncProcess,
		isChecked,
		offsegLoginAuthInfo,
		offsegReregistrationAuthInfo,
		isReregistrationDialogStep1Opened,
		setUserInfo,
		setIsLoginError,
		setIsLoginLockError,
		setIsReregistrationDialogStep1Opened,
		setIsReregistrationDialogStep2Opened,
		setIsRegistrationDialogOpened,
		setNeedsChangeInitPassword,
		setIsLocalStorageError
	};
	const { onClickLogin } = usePostLogin(usePostLoginInput); // ログイン用カスタムフック

	const usePostRegisterInput = {
		setIsAsyncProcess,
		isChecked,
		offsegLoginAuthInfo,
		offsegReregistrationAuthInfo,
		g500LiteRegistrationAuthInfo,
		g500LiteReregistrationAuthInfo,
		isReregistrationDialogStep2Opened,
		setUserInfo,
		setIsRegistrationError,
		setIsLocalStorageError
	};
	const { onClickRegister } = usePostRegister(usePostRegisterInput); // G500Liteログイン情報登録カスタムフック

	return (
		<Box
			id="loginPage"
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				position: 'absolute',
				top: appBar.height,
				height: main.height,
				width: width,
			}}
		>
			{/* アプリ名 */}
			<Box sx={{ display: 'flex', alignItems: 'center', marginTop: 10, marginBottom: 5 }}>
				<Typography
					id="appName"
					role="lable"
					aria-label={loginPageConst.loginPageMain.appName}
					noWrap
					sx={{
						fontSize: 60,
						fontWeight: 'bold',
						color: theme.palette.primary.dark,
					}}
				>
					{loginPageConst.loginPageMain.appName}
				</Typography>
			</Box>
			{/* ログイン情報を入力するテキストフィールド */}
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: loginPageContent.width }}>
				{loginPageConst.loginFromList(offsegLoginAuthInfo, setOffsegLoginAuthInfo).map((loginFrom) => (
					<StyledTextField
						required
						fullWidth
						id={loginFrom.id}
						key={loginFrom.id}
						label={loginFrom.label}
						defaultValue={loginFrom.defaultValue}
						type={loginFrom.type}
						onChange={loginFrom.onChange}
						sx={{ marginBottom: 4 }}
					/>
				))}
			</Box>
			{/* ログイン情報を記憶するか否かのチェックボックス */}
			<Box sx={{ width: loginPageContent.width, marginBottom: 2 }}>
				<FormControlLabel
					id="rememberCheckbox"
					control={<Checkbox checked={isChecked} onChange={onChangeCheckbox} />}
					label={<Typography sx={{ fontSize: 15, color: theme.palette.common.black }}>{loginPageConst.loginPageMain.checkboxName}</Typography>}
				/>
			</Box>
			{/* ログインボタン */}
			<Box sx={{ width: loginPageContent.width, marginBottom: 2 }}>
				<Button id="loginButton" type="submit" fullWidth variant="contained" onClick={onClickLogin}>
					<Typography sx={{ fontSize: 16, color: theme.palette.common.white }}>{loginPageConst.loginPageMain.buttonName}</Typography>
				</Button>
			</Box>
			{/* アラート表示 */}
			<Box sx={{ height: alert.height, width: loginPageContent.width }}>
				{isLoginError && (
					<Alert id="errorAlert" severity="error" onClose={onCloseLoginErrorAlert}>
						<Typography role="lable" aria-label={loginPageConst.common.alert.error} sx={{ fontSize: 13.5, color: theme.palette.common.black }}>
							{loginPageConst.common.alert.error}
						</Typography>
					</Alert>
				)}
				{isLoginLockError && (
					<Alert id="loginLockAlert" severity="error" onClose={onCloseLoginLockErrorAlert}>
						<Typography
							role="lable"
							aria-label={loginPageConst.common.alert.loginLock}
							sx={{ fontSize: 13.5, color: theme.palette.common.black, whiteSpace: 'pre-wrap' }}
						>
							{loginPageConst.common.alert.loginLock}
						</Typography>
					</Alert>
				)}
				{needsChangeInitPassword && (
					<Alert
						id="initPasswordChangeRequestAlert"
						severity="warning"
						action={
							<IconButton id="redirectOffsegLoginPageButton" color="warning" onClick={redirectToOffsegLoginPage} size="small">
								<ArrowForwardIcon />
							</IconButton>
						}
					>
						<Typography
							role="lable"
							aria-label={loginPageConst.common.alert.initPasswordChangeRequest}
							sx={{ fontSize: 13.5, color: theme.palette.common.black }}
						>
							{loginPageConst.common.alert.initPasswordChangeRequest}
						</Typography>
					</Alert>
				)}
				{isLocalStorageError && (
					<Alert id="localStorageAlert" severity="error" onClose={onCloseLocalStorageErrorAlert}>
						<Typography
							role="lable"
							aria-label={loginPageConst.common.alert.localStorage}
							sx={{ fontSize: 13.5, color: theme.palette.common.black, whiteSpace: 'pre-wrap' }}
						>
							{loginPageConst.common.alert.localStorage}
						</Typography>
					</Alert>
				)}
			</Box>
			{/* パスワードを忘れた場合のリンク表示 */}
			<Box sx={{ display: 'flex', justifyContent: 'center', width: loginPageContent.width, marginBottom: 1 }}>
				<Link id="redirectOffsegLoginPageLink" component="button" variant="button" onClick={redirectToOffsegLoginPage}>
					<Typography sx={{ fontSize: 13.5, color: theme.palette.primary.main }}>{loginPageConst.loginPageMain.linkName.forgetPassword}</Typography>
				</Link>
			</Box>
			{/* ログイン情報再登録のリンク表示 */}
			<Box sx={{ display: 'flex', justifyContent: 'center', width: loginPageContent.width }}>
				<Link id="reregistrationLink" component="button" variant="button" onClick={onClickReregistrationLink}>
					<Typography sx={{ fontSize: 13.5, color: theme.palette.primary.main }}>{loginPageConst.loginPageMain.linkName.reregistration}</Typography>
				</Link>
			</Box>
			{/* ログイン情報登録のダイアログ */}
			<Box>
				<LoginPageDialog
					id="registerDialog"
					open={isRegistrationDialogOpened}
					dialogTitle={loginPageConst.registrationDialog.title}
					dialogText={loginPageConst.registrationDialog.text}
					fromList={loginPageConst.registerFromList(g500LiteRegistrationAuthInfo, setG500LiteRegistrationAuthInfo)}
					nextButtonName={loginPageConst.registrationDialog.buttonName.registration}
					onCloseDialog={onCloseRegistrationDialog}
					onClickNext={onClickRegister}
					isError={isRegistrationError}
					isLocalStorageError={isLocalStorageError}
					onCloseErrorAlert={onCloseRegistrationErrorAlert}
					onCloseLocalStorageErrorAlert={onCloseLocalStorageErrorAlert}
				/>
			</Box>
			{/* ログイン情報再登録Step1のダイアログ */}
			<Box>
				<LoginPageDialog
					id="reregisterStep1Dialog"
					open={isReregistrationDialogStep1Opened}
					dialogTitle={loginPageConst.reregistrationStep1Dialog.title}
					dialogText={loginPageConst.reregistrationStep1Dialog.text}
					fromList={loginPageConst.loginFromList(offsegReregistrationAuthInfo, setOffsegReregistrationAuthInfo)}
					nextButtonName={loginPageConst.reregistrationStep1Dialog.buttonName.confirmation}
					onCloseDialog={onCloseReregistrationDialogStep1}
					onClickNext={onClickLogin}
					isError={isLoginError}
					isLoginLockError={isLoginLockError}
					needsChangeInitPassword={needsChangeInitPassword}
					isLocalStorageError={isLocalStorageError}
					onCloseErrorAlert={onCloseLoginErrorAlert}
					onCloseLoginLockErrorAlert={onCloseLoginLockErrorAlert}
					onCloseLocalStorageErrorAlert={onCloseLocalStorageErrorAlert}
					onClickRedirect={redirectToOffsegLoginPage}
				/>
			</Box>
			{/* ログイン情報再登録Step2のダイアログ */}
			<Box>
				<LoginPageDialog
					id="reregisterStep2Dialog"
					open={isReregistrationDialogStep2Opened}
					dialogTitle={loginPageConst.reregistrationStep2Dialog.title}
					dialogText={loginPageConst.reregistrationStep2Dialog.text}
					dialogSupplementaryText={loginPageConst.reregistrationStep2Dialog.supplementaryText}
					companyCode={offsegReregistrationAuthInfo.companyCode}
					userCode={offsegReregistrationAuthInfo.userCode}
					fromList={loginPageConst.registerFromList(g500LiteReregistrationAuthInfo, setG500LiteReregistrationAuthInfo)}
					onCloseDialog={onCloseReregistrationDialogStep2}
					onClickBack={onClickReregistrationDialogStep2BackButton}
					onClickNext={onClickRegister}
					nextButtonName={loginPageConst.reregistrationStep2Dialog.buttonName.registration}
					isError={isRegistrationError}
					isLocalStorageError={isLocalStorageError}
					onCloseErrorAlert={onCloseRegistrationErrorAlert}
					onCloseLocalStorageErrorAlert={onCloseLocalStorageErrorAlert}
				/>
			</Box>
		</Box>
	);
};
